import {Component, Inject, OnInit} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef, MatDialogTitle, MatDialogClose, MatDialogContent, MatDialogActions } from '@angular/material/dialog';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {TagModel} from '../api/models/tag-model';
import {NotificationService} from '../notification/notification.service';
import {TagsService} from './tags.service';
import {AuthService} from '../core/security/auth.service';
import {RolesEnum} from '../core/security/roles.enum';
import {MatConfirmDialogComponent} from '../dialogs/mat-confirm/mat-confirm-dialog.component';
import { FlexModule } from '@angular/flex-layout';
import { MatIconButton, MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { MatTextInputComponent } from '../editors/mat-text-input/mat-text-input.component';
import { ColorInputComponent } from '../editors/color-input/color-input.component';
import { Angular2PromiseButtonModule } from 'angular2-promise-buttons';
import { NgIf } from '@angular/common';

export interface TagEditDialogData {
  editModel: TagModel;
}

@Component({
    selector: 'app-tag-edit-dialog',
    template: `
      <form [formGroup]="form" novalidate (ngSubmit)="onSubmit()">
          <h1 mat-dialog-title fxLayout="row" fxLayoutAlign="space-between center" style="padding-top: 10px;">
              <div fxFlex>{{editModel.id == 'new' ? 'Add Tag' : 'Edit Tag'}}</div>
              <button mat-icon-button [mat-dialog-close]="false" class="button-40">
                  <mat-icon>close</mat-icon>
              </button>
          </h1>
          <mat-dialog-content class="mat-typography">
              <app-mat-text-input [formControl]="nameControl"
                                  label="Tag Name" name="name"
                                  [submitted]="submitted"></app-mat-text-input>

              <app-color-input [formControl]="colorControl" type="text" label="Color" name="color"></app-color-input>
          </mat-dialog-content>
          <mat-dialog-actions align="end">
              <button mat-flat-button type="submit" color="primary" [promiseBtn]="buttonSubmitPromise">Save</button>
              <button mat-flat-button type="button" color="warn"  class="only-icon" *ngIf="hasDeleteAccess && editModel.id !== 'new'"
                      (click)="onDeleteClicked()">
                  <mat-icon>delete</mat-icon>
              </button>
          </mat-dialog-actions>
      </form>
  `,
    styles: [],
    standalone: true,
    imports: [FormsModule, ReactiveFormsModule, MatDialogTitle, FlexModule, MatIconButton, MatDialogClose, MatIcon, CdkScrollable, MatDialogContent, MatTextInputComponent, ColorInputComponent, MatDialogActions, MatButton, Angular2PromiseButtonModule, NgIf]
})
export class TagEditDialogComponent implements OnInit {

  form: UntypedFormGroup;
  nameControl = new UntypedFormControl('', [Validators.required]);
  colorControl = new UntypedFormControl('', [Validators.required]);

  editModel: TagModel = {
    id: 'new',
    name: '',
    color: 'blue'
  };

  submitted: boolean;
  serverError: boolean;
  hasEditAccess: boolean;
  hasDeleteAccess: boolean;
  buttonSubmitPromise: Promise<void>;

  constructor(public dialogRef: MatDialogRef<TagEditDialogComponent>,
              @Inject(MAT_DIALOG_DATA) public data: TagEditDialogData,
              public dialog: MatDialog,
              public tagsService: TagsService,
              private fb: UntypedFormBuilder,
              private notificationService: NotificationService,
              private authService: AuthService
  ) {

  }

  async onSubmit() {
    this.serverError = false;

    if (this.form.valid && this.hasEditAccess) {
      this.buttonSubmitPromise = this.processSubmit();
    }
  }

  async processSubmit(): Promise<void> {

    try {
      this.submitted = true;
      const reqData = this.prepareFormModel();

      if (await this.tagsService.saveTag(reqData)) {
        this.notificationService.notifySuccess('The changes have been submitted successfully');
        this.dialogRef.close(true);
      }
    } catch (e) {
      console.error(e);
      this.notificationService.notifyDanger('Unable to save tag.');
    } finally {
      this.submitted = false;
    }
  }

  prepareFormModel(): TagModel {
    const formModel = this.form.value;

    return {
      id: this.editModel.id,
      name: formModel.name,
      color: formModel.color,
    };
  }

  ngOnInit(): void {
    this.hasEditAccess = this.authService.hasAccess([RolesEnum.Tags_Edit, RolesEnum.Tags_Delete]);
    this.hasDeleteAccess = this.authService.hasAccess([RolesEnum.Tags_Delete]);

    this.createForm();

    if (this.data?.editModel) {
      this.editModel = this.data.editModel;
      this.form.reset({
        name: this.editModel.name,
        color: this.editModel.color
      });
    }
  }

  private createForm() {
    this.form = this.fb.group({
        'name': this.nameControl,
        'color': this.colorControl,
      },
    );
  }

  onDeleteClicked() {
    const dialogRef = this.dialog.open(MatConfirmDialogComponent, {
      // width: '250px',
      // height: '90%',
      data: {
        title: 'Delete Tag',
        actionTitle: 'Delete',
        text: 'Are you sure you want to delete tag?',
        hideCancel: false
      }
    });

    dialogRef.afterClosed().subscribe(async (result) => {
      if (result) {
        await this.tagsService.deleteTag(this.editModel.id!);
        this.notificationService.notifySuccess('The tag successfully deleted');
        this.dialogRef.close(true);

        console.log('end delete');
      }
    });
  }
}
